import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import * as d3 from 'd3';
import CloseIcon from '@material-ui/icons/Close';
import PushPinIcon from '@material-ui/icons/PushPin';
import PushPinOutlinedIcon from '@material-ui/icons/PushPinOutlined';
import { formatDateOnly, getDateAtIndex, usePushParams, parseCrawlDate } from './commons';
import Diff, { LABEL, CONTENT_KEYS } from './Diff';
import { BadgeEl, HttpStatusBadge, ErrBadge, LoopBadge } from './Pills';
import { isRedirectLoop, finalHttpStatus } from './redirectChainStatus';

const FILTERS = [
    { key: 'title',                 label: 'Title' },
    { key: 'metaDescriptions',      label: 'Meta' },
    { key: 'h1s',                   label: 'H1' },
    { key: 'redirectChainElements', label: 'Redirect' },
    { key: 'h2s',                   label: 'H2' },
    { key: 'canonicals',            label: 'Canonical' },
    { key: 'crawlStatus',           label: 'Status' },
];

function extractHttpStatus(pc) {
    const redirectChange = (pc.changes || []).find(c => c.key === 'redirectChainElements');
    const status = finalHttpStatus(redirectChange?.newValue);
    return status !== null ? String(status) : null;
}

function extractCrawlStatus(pc) {
    if (pc.crawlStatus) return pc.crawlStatus;
    const change = (pc.changes || []).find(c => c.key === 'crawlStatus');
    if (change?.newValue) return change.newValue;
    // Detect crawl failure: redirectChainElements changed to empty array
    const redirectChange = (pc.changes || []).find(c => c.key === 'redirectChainElements');
    if (redirectChange && Array.isArray(redirectChange.newValue) && redirectChange.newValue.length === 0) {
        return 'crawl-failed';
    }
    return null;
}


/* Always surface error-related keys regardless of active filter */
function computeVisibleKeys(entry, activeFilters) {
    const changedKeySet = new Set(entry.changes.map(c => c.key));
    let keys = entry.changes.map(c => c.key);

    // For crawl errors, suppress content fields — only redirect/status matter
    if (entry.isCrawlError) {
        keys = keys.filter(k => !CONTENT_KEYS.has(k));
    }

    const filtered = keys.filter(k => activeFilters.length === 0 || activeFilters.includes(k));

    if (activeFilters.length === 0) {
        // For crawl errors, always surface crawlStatus even if it wasn't a changed key
        if (entry.isCrawlError) return [...new Set([...filtered, 'crawlStatus'])];
        return filtered;
    }

    const forced = new Set(filtered);

    // For crawl errors, always show crawl status regardless of filter
    if (entry.isCrawlError) forced.add('crawlStatus');

    // 4xx / 5xx: always show the redirect chain
    const httpCode = parseInt(entry.httpStatus, 10);
    if (httpCode >= 400 && changedKeySet.has('redirectChainElements')) {
        forced.add('redirectChainElements');
    }

    // Crawl failed (empty redirect chain): always show it
    if (entry.crawlStatus === 'crawl-failed' && changedKeySet.has('redirectChainElements')) {
        forced.add('redirectChainElements');
    }

    return [...forced];
}

const fmt = d3.timeFormat('%d %b %Y');

function entryStatusPill(entry) {
    if (entry.isLoop) return <LoopBadge />;
    if (entry.crawlStatus) return <ErrBadge title={entry.crawlStatus}>ERR</ErrBadge>;
    const code = entry.httpStatus ? parseInt(entry.httpStatus, 10) : 200;
    if (code >= 300) return <BadgeEl style={{ background: '#fee2e2', color: '#dc2626' }}>{entry.httpStatus}</BadgeEl>;
    if (entry.afterValues?.indexability?.indexable === false) return <BadgeEl style={{ background: '#fef3c7', color: '#92400e' }}>Non-indexable</BadgeEl>;
    return <BadgeEl style={{ background: '#dbeafe', color: '#1d4ed8' }}>{entry.httpStatus || '200'}</BadgeEl>;
}

function ChangeEntry({ entry, isSelected, onSelect, activeFilters, isExpanded, onToggle, pinned, onUnpin }) {
    const handleExpand = pinned ? () => { onUnpin(); onToggle(); } : onToggle;
    return (
        <EntryWrap $active={isSelected} onClick={() => onSelect(entry.dateFmt)}>
            <EntryHeader>
                <EntryInfo $active={isSelected}>
                    <EntryDateRow>
                        {entryStatusPill(entry)}
                        <EntryDate $active={isSelected} onClick={() => onSelect(entry.dateFmt)}>
                            {fmt(new Date(entry.crawlDate))}
                        </EntryDate>
                    </EntryDateRow>
                    <Tags>
                        {(entry.hitLabels || entry.allLabels).map(label => (
                            <Tag key={label} $highlight={!!entry.hitLabels}>{label}</Tag>
                        ))}
                    </Tags>
                </EntryInfo>
                <EntryActions>
                    <ExpandBtn onClick={e => { e.stopPropagation(); handleExpand(); }}>
                        {isExpanded ? 'Hide diff' : 'Show diff'}
                    </ExpandBtn>
                </EntryActions>
            </EntryHeader>

            {isExpanded && !pinned && (
                <DiffSection>
                    <Diff
                        beforeValues={entry.beforeValues}
                        afterValues={entry.afterValues}
                        changedKeys={computeVisibleKeys(entry, activeFilters)}
                        entryCrawlStatus={entry.crawlStatus}
                        prevLabel={entry.prevCrawlDate ? fmt(new Date(entry.prevCrawlDate)) : 'Before'}
                        currLabel={fmt(new Date(entry.crawlDate))}
                    />
                </DiffSection>
            )}
        </EntryWrap>
    );
}

export default function Drawer({ open, onClose, pinned, onPinChange }) {
    const location = useLocation();
    const pushParams = usePushParams();
    const { pageChanges, curSnapshotDate, numDaysHistory, pageSnapshotsMap } = useSelector(s => s.currentMonitoredUri);

    // ── Read view range from URL (?from=YYYY-MM-DD&to=YYYY-MM-DD)
    const { viewStart, viewEnd } = useMemo(() => {
        const p = new URLSearchParams(location.search);
        return { viewStart: p.get('from') || null, viewEnd: p.get('to') || null };
    }, [location.search]);
    const [active, setActive] = useState([]);
    const [expandedKeys, setExpandedKeys] = useState(new Set());

    const toggle = key => setActive(prev =>
        prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );

    const toggleEntry = useCallback(key => setExpandedKeys(prev => {
        const next = new Set(prev);
        next.has(key) ? next.delete(key) : next.add(key);
        return next;
    }), []);

    const collapseAll = useCallback(() => setExpandedKeys(new Set()), []);

    useEffect(() => { if (pinned) setExpandedKeys(new Set()); }, [pinned]);

    const entries = useMemo(() => {
        if (!Array.isArray(pageChanges)) return [];

        // Sort oldest-first to build a running state of latest known value per key
        const sorted = [...pageChanges].sort((a, b) => new Date(a.crawlDate) - new Date(b.crawlDate));
        const runningState = {};

        const withDiff = sorted.map((pc, idx) => {
            const beforeValues = {};
            const afterValues  = {};
            (pc.changes || []).forEach(c => {
                beforeValues[c.key] = runningState[c.key] ?? null;
                afterValues[c.key]  = c.newValue;
                runningState[c.key] = c.newValue;
            });
            const prevCrawlDate = idx > 0 ? sorted[idx - 1].crawlDate : null;
            // crawlDate alone is not unique — concurrent crawls of the same URI
            // produce entries with identical timestamps.
            const uid = `${pc.crawlDate}#${idx}`;
            return { pc, uid, beforeValues, afterValues, prevCrawlDate };
        });

        const windowStart = formatDateOnly(getDateAtIndex(numDaysHistory - 1));

        return withDiff
            .filter(({ pc }) => {
                if (!pc.changes?.length) return false;
                const ds = formatDateOnly(parseCrawlDate(pc.crawlDate));
                if (ds < windowStart) return false;
                if (viewStart && ds < viewStart) return false;
                if (viewEnd   && ds > viewEnd)   return false;
                return active.length === 0 || pc.changes.some(c => active.includes(c.key));
            })
            .map(({ pc, uid, beforeValues, afterValues, prevCrawlDate }) => {
                const dateFmt = formatDateOnly(parseCrawlDate(pc.crawlDate));
                const extractedStatus = extractCrawlStatus(pc);
                // Fall back to snapshot crawl status if available (snapshot may have been loaded already)
                const snapshotCrawlStatus = pageSnapshotsMap?.[dateFmt]?.crawlStatus;
                const crawlStatus = (extractedStatus === 'crawl-failed' && snapshotCrawlStatus)
                    ? snapshotCrawlStatus
                    : extractedStatus;
                const httpStatus = extractHttpStatus(pc);
                const redirectChange = pc.changes.find(c => c.key === 'redirectChainElements');
                const isLoop = redirectChange ? isRedirectLoop(redirectChange.newValue) : false;
                const isCrawlError = !!(crawlStatus || (httpStatus && parseInt(httpStatus, 10) >= 300));
                const relevantChanges = isCrawlError
                    ? pc.changes.filter(c => !CONTENT_KEYS.has(c.key))
                    : pc.changes;
                const allLabels = [...new Set(relevantChanges.map(c => LABEL[c.key] || c.key))];
                const hitLabels = active.length
                    ? [...new Set(relevantChanges.filter(c => active.includes(c.key)).map(c => LABEL[c.key] || c.key))]
                    : null;
                return {
                    uid,
                    crawlDate:    pc.crawlDate,
                    prevCrawlDate,
                    dateFmt,
                    allLabels,
                    hitLabels,
                    changes:      pc.changes,
                    beforeValues,
                    afterValues,
                    httpStatus,
                    crawlStatus,
                    isCrawlError,
                    isLoop,
                };
            })
            .sort((a, b) => new Date(b.crawlDate) - new Date(a.crawlDate));
    }, [pageChanges, active, viewStart, viewEnd, numDaysHistory, pageSnapshotsMap]);

    const totalCount = useMemo(() => {
        if (!Array.isArray(pageChanges)) return 0;
        const windowStart = formatDateOnly(getDateAtIndex(numDaysHistory - 1));
        return pageChanges.filter(pc => pc.changes?.length > 0 && formatDateOnly(parseCrawlDate(pc.crawlDate)) >= windowStart).length;
    }, [pageChanges, numDaysHistory]);

    const isFiltered = active.length > 0 || viewStart || viewEnd;

    const groupedEntries = useMemo(() => {
        const groups = [];
        let currentKey = null;
        entries.forEach(e => {
            const key = d3.timeFormat('%B %Y')(new Date(e.crawlDate));
            if (key !== currentKey) {
                groups.push({ label: key, entries: [] });
                currentKey = key;
            }
            groups[groups.length - 1].entries.push(e);
        });
        return groups;
    }, [entries]);

    const expandAll = useCallback(() => setExpandedKeys(new Set(entries.map(e => e.uid))), [entries]);

    // Auto-expand all visible entries when a filter is activated
    useEffect(() => {
        if (active.length > 0) setExpandedKeys(new Set(entries.map(e => e.uid)));
    }, [active.join(',')]); // intentional: only re-run when filter keys change, not on every entries reference

    const select = dateFmt => {
        pushParams({ date: dateFmt });
        onClose();
    };

    const resetDateRange = useCallback(() => pushParams({ from: null, to: null }), [pushParams]);

    const visible = open || pinned;

    return (
        <>
            {!pinned && <Backdrop $open={open} onClick={onClose} />}
            <Panel $open={visible} $pinned={pinned} $visible={visible}>
                <Header>
                    <Title>Change Log</Title>
                    {isFiltered && (
                        <ResetBtn onClick={() => { setActive([]); resetDateRange(); }}>
                            Reset filters
                        </ResetBtn>
                    )}
                    <HeaderButtons>
                        <PinBtn onClick={() => onPinChange(!pinned)} $pinned={pinned} title={pinned ? 'Unpin drawer' : 'Pin drawer open'}>
                            {pinned ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
                        </PinBtn>
                        <CloseBtn onClick={() => { if (pinned) onPinChange(false); onClose(); }} aria-label="Close"><CloseIcon fontSize="small" /></CloseBtn>
                    </HeaderButtons>
                </Header>

                <FilterSection>
                    <FilterLabel>Filter by change type</FilterLabel>
                    <FilterRow>
                        {FILTERS.map(f => (
                            <FilterChip key={f.key} $on={active.includes(f.key)} onClick={() => toggle(f.key)}>
                                {f.label}
                            </FilterChip>
                        ))}
                    </FilterRow>
                    {active.length > 0 && (
                        <ClearAll onClick={() => setActive([])}>Clear</ClearAll>
                    )}
                </FilterSection>

                <CountRow>
                    <Count>
                        {isFiltered
                            ? <><strong>{entries.length}</strong> of {totalCount} changes</>
                            : <>{entries.length} {entries.length === 1 ? 'change' : 'changes'}</>
                        }
                    </Count>
                    {entries.length > 0 && (
                        <>
                            <ExpandAllBtn onClick={expandAll}>expand all</ExpandAllBtn>
                            <ExpandAllBtn onClick={collapseAll}>collapse all</ExpandAllBtn>
                        </>
                    )}
                </CountRow>

                {isFiltered && (
                    <FilterBanner>
                        <FilterBannerLeft>
                            Filters:
                            {(viewStart || viewEnd) && (
                                <FilterTag>
                                    {viewStart ? d3.timeFormat('%d %b')(new Date(viewStart + 'T00:00:00')) : '…'}
                                    {' – '}
                                    {viewEnd ? d3.timeFormat('%d %b')(new Date(viewEnd + 'T00:00:00')) : '…'}
                                    <TagClear onClick={resetDateRange} title="Clear date range">×</TagClear>
                                </FilterTag>
                            )}
                            {active.map(k => (
                                <FilterTag key={k}>
                                    {LABEL[k] || k}
                                    <TagClear onClick={() => toggle(k)}>×</TagClear>
                                </FilterTag>
                            ))}
                        </FilterBannerLeft>
                        <FilterBannerClear onClick={() => { setActive([]); resetDateRange(); }}>
                            Clear all
                        </FilterBannerClear>
                    </FilterBanner>
                )}

                <List>
                    {entries.length === 0
                        ? <Empty>No matching entries</Empty>
                        : groupedEntries.map(group => (
                            <React.Fragment key={group.label}>
                                <MonthHeader>{group.label}</MonthHeader>
                                {group.entries.map(e => (
                                    <ChangeEntry
                                        key={e.uid}
                                        entry={e}
                                        isSelected={e.dateFmt === curSnapshotDate}
                                        onSelect={select}
                                        activeFilters={active}
                                        isExpanded={expandedKeys.has(e.uid)}
                                        onToggle={() => toggleEntry(e.uid)}
                                        pinned={pinned}
                                        onUnpin={() => onPinChange(false)}
                                    />
                                ))}
                            </React.Fragment>
                        ))
                    }
                </List>
            </Panel>
        </>
    );
}

const Backdrop = styled.div`
    display: ${({ $open }) => $open ? 'block' : 'none'};
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.25);
    z-index: 1200;
`;

const Panel = styled.div`
    background: #fff;
    display: flex;
    flex-direction: column;

    /* ── docked (pinned) ── */
    position: ${({ $pinned }) => $pinned ? 'sticky' : 'fixed'};
    top: ${({ $pinned }) => $pinned ? '16px' : '0'};
    right: ${({ $pinned }) => $pinned ? 'auto' : '0'};
    width: ${({ $pinned }) => $pinned ? '340px' : 'min(680px, 100vw)'};
    height: ${({ $pinned }) => $pinned ? 'calc(100dvh - 32px)' : '100dvh'};
    flex-shrink: ${({ $pinned }) => $pinned ? '0' : 'unset'};
    border: ${({ $pinned }) => $pinned ? '1px solid #e2e8f0' : 'none'};
    border-left: ${({ $pinned }) => $pinned ? 'none' : '1px solid #e2e8f0'};
    border-radius: ${({ $pinned }) => $pinned ? '12px' : '0'};
    overflow: ${({ $pinned }) => $pinned ? 'hidden' : 'visible'};
    box-shadow: ${({ $pinned }) => $pinned ? 'none' : '-4px 0 24px rgba(0,0,0,0.1)'};
    z-index: ${({ $pinned }) => $pinned ? 'auto' : '1201'};
    transform: ${({ $pinned, $open }) => $pinned ? 'none' : ($open ? 'translateX(0)' : 'translateX(100%)')};
    transition: ${({ $pinned }) => $pinned ? 'none' : 'transform 220ms cubic-bezier(0.4,0,0.2,1)'};
`;

const Header = styled.div`
    display: flex; align-items: center; justify-content: space-between;
    padding: 16px 18px; border-bottom: 1px solid #e2e8f0; flex-shrink: 0;
`;

const Title = styled.div`
    font-size: 0.88rem; font-weight: 700; color: #0f172a;
`;

const ResetBtn = styled.button`
    margin-left: auto;
    margin-right: 4px;
    background: none;
    border: none;
    padding: 4px 10px;
    font-size: 0.72rem;
    font-weight: 600;
    color: #6366f1;
    cursor: pointer;
    border-radius: 6px;
    transition: background 120ms;
    &:hover { background: #ede9fe; }
`;

const PinBtn = styled.button`
    display: none;
    @media (min-width: 768px) {
        display: inline-flex;
        align-items: center; justify-content: center;
        width: 30px; height: 30px; border: none; border-radius: 7px;
        background: ${({ $pinned }) => $pinned ? '#ede9fe' : 'transparent'};
        color: ${({ $pinned }) => $pinned ? '#6366f1' : '#64748b'};
        cursor: pointer;
        transition: background 120ms, color 120ms;
        &:hover { background: #ede9fe; color: #6366f1; }
    }
`;

const HeaderButtons = styled.div`
    display: flex;
    align-items: center;
    gap: 2px;
    margin-left: auto;
`;

const CloseBtn = styled.button`
    display: inline-flex; align-items: center; justify-content: center;
    width: 30px; height: 30px; border: none; border-radius: 7px;
    background: transparent; color: #64748b; cursor: pointer;
    transition: background 120ms, color 120ms;
    &:hover { background: #f1f5f9; color: #0f172a; }
`;

const FilterSection = styled.div`
    padding: 10px 16px 8px; border-bottom: 1px solid #f1f5f9; flex-shrink: 0;
`;

const FilterLabel = styled.div`
    font-size: 0.62rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #94a3b8;
    margin-bottom: 8px;
`;

const FilterRow = styled.div`
    display: flex; flex-wrap: wrap; gap: 6px;
`;

const FilterChip = styled.button`
    padding: 4px 10px; border-radius: 99px;
    font-size: 0.72rem; font-weight: 600; cursor: pointer;
    border: 1px solid ${({ $on }) => $on ? '#6366f1' : '#e2e8f0'};
    background: ${({ $on }) => $on ? '#ede9fe' : '#f8fafc'};
    color: ${({ $on }) => $on ? '#6366f1' : '#64748b'};
    transition: all 120ms;
    &:hover { border-color: #6366f1; color: #6366f1; background: #ede9fe; }
`;

const ClearAll = styled.button`
    margin-top: 8px; background: none; border: none;
    font-size: 0.7rem; color: #94a3b8; cursor: pointer; padding: 0;
    &:hover { color: #6366f1; }
`;

const CountRow = styled.div`
    padding: 8px 18px;
    border-bottom: 1px solid #f1f5f9;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
`;

const Count = styled.span`
    font-size: 0.72rem; color: #94a3b8; font-weight: 500;
`;

const ExpandAllBtn = styled.button`
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 3px 10px;
    font-size: 0.72rem;
    color: #64748b;
    cursor: pointer;
    font-weight: 600;
    transition: all 120ms;
    &:hover { border-color: #6366f1; color: #6366f1; background: #ede9fe; }
`;

const FilterBanner = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 18px;
    background: #fefce8;
    border-bottom: 1px solid #fde68a;
    flex-wrap: wrap;
`;

const FilterBannerLeft = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    font-size: 0.7rem;
    font-weight: 600;
    color: #92400e;
`;

const FilterTag = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: #fef3c7;
    border: 1px solid #fde68a;
    border-radius: 99px;
    padding: 1px 8px;
    font-size: 0.68rem;
    font-weight: 600;
    color: #92400e;
`;

const TagClear = styled.button`
    background: none;
    border: none;
    padding: 0;
    line-height: 1;
    font-size: 0.85rem;
    color: #92400e;
    cursor: pointer;
    opacity: 0.6;
    &:hover { opacity: 1; }
`;

const FilterBannerClear = styled.button`
    background: none;
    border: none;
    padding: 0;
    font-size: 0.7rem;
    font-weight: 700;
    color: #92400e;
    cursor: pointer;
    white-space: nowrap;
    text-decoration: underline;
    &:hover { color: #78350f; }
`;

const MonthHeader = styled.div`
    padding: 6px 18px 4px;
    font-size: 0.62rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: #94a3b8;
    background: #f8fafc;
    border-bottom: 1px solid #f1f5f9;
    position: sticky;
    top: 0;
    z-index: 1;
`;

const List = styled.div`
    overflow-y: auto;
    flex: 1;
    min-height: 0;
`;

const EntryWrap = styled.div`
    border-bottom: 1px solid #f0f0f0;
    background: ${({ $active }) => $active ? '#f5f3ff' : 'transparent'};
    cursor: pointer;
    &:hover { background: ${({ $active }) => $active ? '#f5f3ff' : '#fafafa'}; }
`;

const EntryHeader = styled.div`
    display: flex; align-items: flex-start;
`;

const EntryInfo = styled.div`
    flex: 1; padding: 12px 18px;
`;

const EntryActions = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 10px 10px 10px 0;
    flex-shrink: 0;
`;


const CrawlErrText = styled.div`
    font-size: 0.7rem;
    color: #dc2626;
    margin-top: 4px;
    line-height: 1.4;
`;

const EntryDateRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
`;


const EntryDate = styled.div`
    font-size: 0.78rem; font-weight: 700;
    color: ${({ $active }) => $active ? '#6366f1' : '#0f172a'};
    cursor: pointer;
    &:hover { color: #6366f1; text-decoration: underline; }
`;

const Tags = styled.div`
    display: flex; flex-wrap: wrap; gap: 4px;
`;

const Tag = styled.span`
    font-size: 0.65rem; font-weight: 600; padding: 2px 7px; border-radius: 4px;
    background: ${({ $highlight }) => $highlight ? '#ede9fe' : '#f1f5f9'};
    color: ${({ $highlight }) => $highlight ? '#6366f1' : '#64748b'};
`;

const ExpandBtn = styled.button`
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 3px 10px;
    font-size: 0.72rem;
    font-weight: 600;
    color: #64748b;
    cursor: pointer;
    white-space: nowrap;
    transition: all 120ms;
    &:hover { border-color: #6366f1; color: #6366f1; background: #ede9fe; }
`;

const DiffSection = styled.div`
    border-top: 1px solid #f1f5f9;
    overflow-x: auto;
`;


const Empty = styled.div`
    padding: 24px 18px; font-size: 0.8rem; color: #94a3b8; text-align: center;
`;
